import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CssBaseline,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Input,
  Link,
  Modal,
  ModalClose,
  ModalDialog,
  Snackbar,
  Stack,
  Textarea,
  Tooltip,
  Typography,
} from '@mui/joy';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add,
  Delete as DeleteIcon,
  DeleteSweep as DeleteSweepIcon,
  Edit,
  ExpandMore,
  KeyboardArrowRight,
  Launch as LaunchIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
  Subtitles as SubtitlesIcon,
} from '@mui/icons-material';
import DeleteAlert from '../../main/widgets/DeleteAlert';
import CustomTextField from '../../main/widgets/CustomTextField';
import LocationOptionList from '../../main/widgets/LocationOptionList';
import StatusOptionList from '../../main/widgets/StatusOptionList';
import TrackingEventTypeOptionList from '../../main/widgets/TrackingEventTypeOptionList';
import { PERMISSIONS } from '../../main/context/AuthContext';
import { usePermissions } from '../../main/hooks/usePermissions';
import PermissionGate from '../../main/components/PermissionGate';
import { DirectionalMaterialThemeProvider } from '../../main/theme/DirectionalMaterialThemeProvider';
import { dataGridNativeCheckboxSlots } from '../../main/components/dataGridNativeCheckboxSlots';
import { OmrAmount } from '../../main/components/OmrAmount';
import OmrSymbol from '../../main/components/OmrSymbol';
import { getLocationDisplayName, toLocationWritePk } from '../../main/utils/locationDisplayName';
import { labelForEventTypeCode } from '../../main/utils/trackingEventLabels';
import useTrackingEventTypeOptions from '../../main/hooks/useTrackingEventTypeOptions';
import { translateAxiosErrorMessage } from '../../main/utils/masterApiError';
import {
  shipmentStatusChipSx,
  shipmentStatusJoyColor,
  shipmentStatusLabel,
} from '../../main/utils/shipmentStatusDisplay';
import {
  deleteShipmentPiece,
  extractShipmentPieceFieldErrors,
  getShipmentPiece,
  listShipmentPieces,
  patchShipmentPiece,
  previewChargeableWeightKg,
  previewVolumetricWeightKg,
  recoverShipmentPieces,
} from '../../main/API/shipmentPiece';
import CreateShipmentPieceModal from './widgets/CreateShipmentPieceModal';
import ShipmentPiecesStickersWindow from './widgets/ShipmentPiecesStickersWindow';
import { useTrackingEventPrompt } from '../../main/hooks/useTrackingEventPrompt';
import {
  applyTrackingEventControl,
  buildPieceTrackingSnapshot,
} from '../../main/utils/trackingEventControl';

function formatDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (_) {
    return '—';
  }
}

const dataGridSx = {
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  overflow: 'hidden',
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  '& .MuiDataGrid-root': { border: 'none' },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid #e0e0e0',
    fontSize: '14px',
    fontWeight: 600,
    color: '#333',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: 600,
      fontSize: '14px',
      color: '#333',
    },
  },
  '& .MuiDataGrid-cell': {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: '1.5',
    padding: '12px 16px',
    fontSize: '14px',
    color: '#555',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    borderBottom: '1px solid #f0f0f0',
  },
  '& .MuiDataGrid-row': {
    '&:hover': { backgroundColor: '#f8f9fa', cursor: 'pointer' },
    '&.Mui-selected': {
      backgroundColor: '#e3f2fd',
      '&:hover': { backgroundColor: '#bbdefb' },
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '2px solid #e0e0e0',
    backgroundColor: '#fafafa',
    fontSize: '14px',
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  '@media (max-width: 600px)': {
    '& .MuiDataGrid-cell': {
      minHeight: '60px',
      alignItems: 'center',
      padding: '8px',
      fontSize: '13px',
    },
    '& .MuiDataGrid-columnHeaders': { fontSize: '13px' },
  },
};

export default function ShipmentPiecesManagerScreen() {
  const { t, i18n } = useTranslation('ui');
  const { hasPermission } = usePermissions();

  const canList = hasPermission(PERMISSIONS.SHIPMENT_PIECES_LIST);
  const canView = hasPermission(PERMISSIONS.SHIPMENT_PIECES_VIEW);
  const canCreate = hasPermission(PERMISSIONS.SHIPMENT_PIECES_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.SHIPMENT_PIECES_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.SHIPMENT_PIECES_DELETE);
  const canRecover = hasPermission(PERMISSIONS.SHIPMENT_PIECES_RECOVER);
  const canPrintLabel = hasPermission(PERMISSIONS.SHIPMENT_PIECES_LABEL);
  const canPrintBatch = hasPermission(PERMISSIONS.SHIPMENT_PIECES_LABEL_BATCH);
  const canListLocations = hasPermission(PERMISSIONS.LOCATIONS_LIST);
  const canListEventTypes = hasPermission(PERMISSIONS.SHIPMENT_TRACKING_EVENT_TYPES_LIST);

  const { options: eventTypeOptions } = useTrackingEventTypeOptions(canListEventTypes);
  const { promptIfNeeded, trackingPromptModal } = useTrackingEventPrompt();
  const editOriginalTrackingSnapshotRef = useRef(null);

  const [data, setData] = useState(null);
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
  const [viewMode, setViewMode] = useState('active');

  const [shipmentId, setShipmentId] = useState('');
  const [barcodeContains, setBarcodeContains] = useState('');
  const [searchText, setSearchText] = useState('');
  const [status, setStatus] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editPiece, setEditPiece] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editEventTypeOpt, setEditEventTypeOpt] = useState(null);
  const [editLocation, setEditLocation] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editFieldErrors, setEditFieldErrors] = useState({});

  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deleteAlertSingle, setDeleteAlertSingle] = useState(false);
  const [selectedPieceIds, setSelectedPieceIds] = useState([]);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [piecesStickersWindowOpened, setPiecesStickersWindowOpened] = useState(false);
  const [recoverLoading, setRecoverLoading] = useState(false);
  const [openRecoverAlert, setOpenRecoverAlert] = useState(false);

  const [successDelete, setSuccessDelete] = useState(false);
  const [failedDelete, setFailedDelete] = useState(false);
  const [deleteErrorMsg, setDeleteErrorMsg] = useState('');
  const [successNotification, setSuccessNotification] = useState(false);
  const [failedNotification, setFailedNotification] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  const handlePageSizeChange = (pageSize) => {
    setPaginationModel({ page: 0, pageSize });
  };

  const handlePageChange = (page) => {
    setPaginationModel((prev) => ({ ...prev, page }));
  };

  const fetchData = async (isSearch = false) => {
    if (!canList) {
      setData(null);
      setResults([]);
      setIsLoading(false);
      return;
    }
    if (isSearch) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
    setIsLoading(true);
    const params = {
      page_size: paginationModel.pageSize,
      page: isSearch ? 1 : paginationModel.page + 1,
    };
    if (viewMode === 'deleted') params.deleted = 'true';
    const sid = String(shipmentId || '').trim();
    if (sid) params.shipment = sid;
    const bc = String(barcodeContains || '').trim();
    if (bc) params.barcode_contains = bc;
    const st = String(searchText || '').trim();
    if (st) params.search = st;
    if (status) params.status = status;
    if (eventTypeFilter && eventTypeFilter.code) params.event_type = eventTypeFilter.code;
    if (currentLocation && currentLocation.id) params.location = currentLocation.id;

    try {
      const response = await listShipmentPieces(params);
      const actualData = response?.data;
      setData(actualData ?? null);
      if (actualData && Array.isArray(actualData.results)) {
        setResults(actualData.results);
      } else if (Array.isArray(actualData)) {
        setResults(actualData);
      } else {
        setResults([]);
      }
    } catch (err) {
      console.log(err?.message);
      setResults([]);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize, viewMode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    await fetchData(true);
  };

  const deletePiecesFromTable = async (ids) => {
    if (!canDelete || !ids?.length) {
      setDeleteAlert(false);
      setDeleteAlertSingle(false);
      return;
    }
    setDeleteLoading(true);
    try {
      await Promise.all(ids.map((id) => deleteShipmentPiece(id)));
      setSuccessDelete(true);
      setSelectionModel([]);
      await fetchData(true);
    } catch (e) {
      setFailedDelete(true);
      setDeleteErrorMsg(
        translateAxiosErrorMessage(e, t) || t('shipmentPieces.deleteErrorGeneric')
      );
    } finally {
      setDeleteLoading(false);
      setDeleteAlert(false);
      setDeleteAlertSingle(false);
    }
  };

  const canPrintSelection =
    selectionModel.length === 1
      ? canPrintLabel
      : selectionModel.length > 1
        ? canPrintBatch
        : false;

  const selectedPieces = useMemo(
    () =>
      (results ?? []).filter((piece) =>
        selectionModel.some((id) => id === piece.id)
      ),
    [results, selectionModel]
  );

  const handleRecoverPieces = async () => {
    if (selectionModel.length === 0 || !canRecover) return;
    setRecoverLoading(true);
    setOpenRecoverAlert(false);
    try {
      const response = await recoverShipmentPieces(selectionModel);
      const res = response && response.data;
      const recovered = (res && res.recovered_count) != null ? res.recovered_count : 0;
      const skipped = (res && res.skipped_ids) || [];
      setSelectionModel([]);
      await fetchData(true);
      if (recovered > 0) {
        const msg = skipped.length > 0
          ? t('shipmentPieces.recoveredWithSkipped', { recovered, skipped: skipped.join(', ') })
          : t('shipmentPieces.recoveredToActive', { count: recovered });
        setResponseMessage(msg);
        setSuccessNotification(true);
      }
      if (recovered === 0 && skipped.length > 0) {
        setResponseMessage(t('shipmentPieces.noneRecoveredSkipped', { skipped: skipped.join(', ') }));
        setFailedNotification(true);
      }
    } catch (err) {
      const res = err && err.response;
      const msg = (res && res.status === 403)
        ? t('shipmentPieces.permissionRecover')
        : (res && res.data && res.data.error) || (err && err.message) || t('shipmentPieces.failedRecover');
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setRecoverLoading(false);
    }
  };

  const handleRecoverOnePiece = async (pieceId) => {
    if (!canRecover) return;
    setRecoverLoading(true);
    try {
      const response = await recoverShipmentPieces([pieceId]);
      const res = response && response.data;
      const recovered = (res && res.recovered_count) != null ? res.recovered_count : 0;
      const skipped = (res && res.skipped_ids) || [];
      await fetchData(true);
      if (recovered > 0) {
        setResponseMessage(t('shipmentPieces.pieceRecoveredSingle'));
        setSuccessNotification(true);
      } else if (skipped.length > 0) {
        setResponseMessage(t('shipmentPieces.couldNotRecoverSingle', { skipped: skipped.join(', ') }));
        setFailedNotification(true);
      }
    } catch (err) {
      const res = err && err.response;
      const msg = (res && res.status === 403)
        ? t('shipmentPieces.permissionRecover')
        : (res && res.data && res.data.error) || (err && err.message) || t('shipmentPieces.failedRecoverSingle');
      setResponseMessage(msg);
      setFailedNotification(true);
    } finally {
      setRecoverLoading(false);
    }
  };

  const openEdit = useCallback(
    async (row) => {
      if (!canView) return;
      try {
        const res = await getShipmentPiece(row.id);
        const piece = res?.data || row;
        setEditPiece(piece);
        const pieceForm = {
          sequence: piece.sequence == null ? '' : String(piece.sequence),
          description: piece.description || '',
          weight_kg: piece.weight_kg == null ? '' : String(piece.weight_kg),
          width_cm: piece.width_cm == null ? '' : String(piece.width_cm),
          length_cm: piece.length_cm == null ? '' : String(piece.length_cm),
          height_cm: piece.height_cm == null ? '' : String(piece.height_cm),
          volumetric_weight_kg:
            piece.volumetric_weight_kg == null ? '' : String(piece.volumetric_weight_kg),
          status: piece.status || '',
          event_type: piece.event_type || '',
          note: piece.note || '',
          product_category_code:
            piece.product_category_code == null ? '' : String(piece.product_category_code),
          meter_price_override:
            piece.meter_price_override == null ? '' : String(piece.meter_price_override),
          extra_cost: piece.extra_cost == null ? '' : String(piece.extra_cost),
        };
        setEditForm(pieceForm);
        setEditLocation(piece.location && typeof piece.location === 'object' ? piece.location : null);
        setEditEventTypeOpt(
          piece.event_type ? { code: piece.event_type, name: piece.event_type } : null,
        );
        editOriginalTrackingSnapshotRef.current = buildPieceTrackingSnapshot(
          pieceForm,
          piece.location && typeof piece.location === 'object' ? piece.location : null,
        );
        setEditFieldErrors({});
      } catch (e) {
        setResponseMessage(translateAxiosErrorMessage(e, t) || t('shipmentPieces.errorLoad'));
        setFailedNotification(true);
      }
    },
    [canView, t]
  );

  const closeEdit = () => {
    setEditPiece(null);
    setEditForm({});
    setEditEventTypeOpt(null);
    setEditLocation(null);
    setEditFieldErrors({});
  };

  const saveEdit = async () => {
    if (!editPiece || !canUpdate) return;
    setSavingEdit(true);
    setEditFieldErrors({});
    try {
      const trackingDecision = await promptIfNeeded({
        isCreate: false,
        original: editOriginalTrackingSnapshotRef.current,
        current: buildPieceTrackingSnapshot(editForm, editLocation),
        entityType: 'shipment_piece',
      });
      if (trackingDecision === null) {
        return;
      }
      const payload = {};
      const numKeys = [
        'sequence',
        'weight_kg',
        'width_cm',
        'length_cm',
        'height_cm',
        'volumetric_weight_kg',
        'meter_price_override',
        'extra_cost',
      ];
      Object.entries(editForm).forEach(([k, v]) => {
        if (v === '' || v == null) {
          payload[k] = numKeys.includes(k) ? null : k === 'status' || k === 'event_type' ? '' : null;
        } else if (numKeys.includes(k)) {
          payload[k] = k === 'sequence' ? Number(v) : v;
        } else {
          payload[k] = v;
        }
      });
      payload.location = toLocationWritePk(editLocation);
      const finalPayload = applyTrackingEventControl(payload, trackingDecision);
      const res = await patchShipmentPiece(editPiece.id, finalPayload);
      const updated = res?.data;
      setResponseMessage(t('shipmentPieces.savedSuccess'));
      setSuccessNotification(true);
      closeEdit();
      await fetchData(true);
      if (updated?.id) {
        setSelectionModel((prev) => prev.filter((id) => id !== updated.id));
      }
    } catch (e) {
      const errs = extractShipmentPieceFieldErrors(e);
      if (Object.keys(errs).length) setEditFieldErrors(errs);
      setResponseMessage(translateAxiosErrorMessage(e, t) || t('shipmentPieces.errorLoad'));
      setFailedNotification(true);
    } finally {
      setSavingEdit(false);
    }
  };

  const editVolumetricPreview = useMemo(() => {
    const dimsFilled = [editForm.width_cm, editForm.length_cm, editForm.height_cm].filter(
      (v) => v != null && v !== ''
    ).length;
    if (dimsFilled !== 3) return null;
    return previewVolumetricWeightKg(editForm.width_cm, editForm.length_cm, editForm.height_cm);
  }, [editForm.width_cm, editForm.length_cm, editForm.height_cm]);

  const editChargeablePreview = useMemo(
    () =>
      previewChargeableWeightKg(
        editForm.weight_kg,
        editForm.volumetric_weight_kg !== '' && editForm.volumetric_weight_kg != null
          ? editForm.volumetric_weight_kg
          : editVolumetricPreview
      ),
    [editForm.weight_kg, editForm.volumetric_weight_kg, editVolumetricPreview]
  );

  const columns = useMemo(
    () => [
      { field: 'id', headerName: t('shipmentPieces.idCol'), flex: 1, minWidth: 70 },
      {
        field: 'human_label',
        headerName: t('shipmentPieces.humanLabel'),
        flex: 1.5,
        minWidth: 110,
        renderCell: (params) => params.row.human_label || '—',
      },
      {
        field: 'barcode',
        headerName: t('shipmentPieces.barcode'),
        flex: 2,
        minWidth: 160,
        renderCell: (params) => (
          <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
            {params.row.barcode || '—'}
          </Typography>
        ),
      },
      {
        field: 'shipment',
        headerName: t('shipmentPieces.shipmentCol'),
        flex: 1.2,
        minWidth: 100,
        renderCell: (params) => {
          const sid = params.row.shipment;
          if (!sid) return '—';
          return (
            <Link
              href={`/Shipment/${sid}/EditShipment`}
              target="_blank"
              rel="noopener"
              onClick={(e) => e.stopPropagation()}
            >
              #{sid}
            </Link>
          );
        },
      },
      {
        field: 'sequence',
        headerName: t('shipmentPieces.sequence'),
        flex: 0.8,
        minWidth: 70,
        type: 'number',
      },
      {
        field: 'status',
        headerName: t('shipmentPieces.status'),
        flex: 1.5,
        minWidth: 130,
        renderCell: (params) =>
          params.row.status ? (
            <Chip
              size="sm"
              color={shipmentStatusJoyColor(params.row.status)}
              variant="soft"
              sx={shipmentStatusChipSx(params.row.status)}
            >
              {shipmentStatusLabel(params.row.status, t)}
            </Chip>
          ) : (
            '—'
          ),
      },
      {
        field: 'location',
        headerName: t('shipmentPieces.location'),
        flex: 2,
        minWidth: 140,
        renderCell: (params) =>
          params.row.location
            ? getLocationDisplayName(params.row.location, i18n.language) || params.row.location.name || '—'
            : '—',
      },
      {
        field: 'chargeable_weight_kg',
        headerName: t('shipmentPieces.chargeableWeight'),
        flex: 1.2,
        minWidth: 110,
        renderCell: (params) =>
          params.row.chargeable_weight_kg != null && params.row.chargeable_weight_kg !== ''
            ? `${params.row.chargeable_weight_kg} kg`
            : '—',
      },
      {
        field: 'price',
        headerName: t('shipmentPieces.piecePrice'),
        flex: 1.2,
        minWidth: 100,
        renderHeader: () => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <span>{t('shipmentPieces.piecePrice')}</span>
            <OmrSymbol height={14} />
          </Box>
        ),
        renderCell: (params) => <OmrAmount value={params.row.price} empty="—" />,
      },
      {
        field: 'event_type',
        headerName: t('shipmentPieces.eventType'),
        flex: 1.8,
        minWidth: 150,
        renderCell: (params) =>
          params.row.event_type
            ? labelForEventTypeCode(params.row.event_type, eventTypeOptions)
            : '—',
      },
      {
        field: 'modified_at',
        headerName: t('shipmentPieces.lastEvent'),
        flex: 2,
        minWidth: 150,
        renderCell: (params) => formatDateTime(params.row.modified_at),
      },
      {
        field: 'actions',
        headerName: t('shipments.colActions'),
        flex: 2.5,
        minWidth: 140,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canUpdate && (
              <Tooltip title={t('shipmentPieces.tooltipEditPiece')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(params.row);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {params.row.barcode && (
              <Tooltip title={t('shipmentPieces.actions.viewTracking')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="neutral"
                  component="a"
                  href={`/track/${encodeURIComponent(params.row.barcode)}`}
                  target="_blank"
                  rel="noopener"
                  onClick={(e) => e.stopPropagation()}
                >
                  <LaunchIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title={t('shipmentPieces.tooltipDeletePiece')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedPieceIds([params.row.id]);
                    setDeleteAlertSingle(true);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [t, i18n.language, eventTypeOptions, canUpdate, canDelete, openEdit]
  );

  const columnsDeleted = useMemo(
    () => [
      ...columns.slice(0, -1),
      {
        field: 'actions',
        headerName: t('shipments.colActions'),
        flex: 1.5,
        minWidth: 80,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canRecover && (
              <Tooltip title={t('shipmentPieces.tooltipRecoverPiece')}>
                <IconButton
                  size="sm"
                  variant="soft"
                  color="success"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRecoverOnePiece(params.row.id);
                  }}
                >
                  <RestoreFromTrashIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      },
    ],
    [columns, t, canRecover, handleRecoverOnePiece]
  );

  if (!canList) {
    return (
      <DirectionalMaterialThemeProvider>
        <CssBaseline />
        <Box sx={{ p: 4 }}>
          <Typography level="body-md" color="danger">
            {t('permissions.noPermission')}
          </Typography>
        </Box>
      </DirectionalMaterialThemeProvider>
    );
  }

  return (
    <DirectionalMaterialThemeProvider>
      <CssBaseline />
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex' }} m={3}>
          <Box width="100%">
            <Box>
              <Breadcrumbs separator={<KeyboardArrowRight />} aria-label="breadcrumbs">
                {[t('shipmentPieces.breadcrumb1'), t('shipmentPieces.breadcrumb2')].map((item) => (
                  <Link key={item} color="neutral" href="/shipment/pieces">
                    {item}
                  </Link>
                ))}
              </Breadcrumbs>
              <Typography textAlign="start" level="h1">
                {t('shipmentPieces.pageTitle')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, mb: 1, flexWrap: 'wrap' }}>
              <Typography level="body-sm" sx={{ fontWeight: 600, color: 'text.secondary', mr: 0.5 }}>
                {t('shipments.viewLabel')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button
                  size="sm"
                  variant={viewMode === 'active' ? 'soft' : 'outlined'}
                  color="primary"
                  onClick={() => {
                    setViewMode('active');
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    setSelectionModel([]);
                  }}
                >
                  {t('shipmentPieces.activeTab')}
                </Button>
                <Button
                  size="sm"
                  variant={viewMode === 'deleted' ? 'soft' : 'outlined'}
                  color="neutral"
                  startDecorator={<DeleteSweepIcon />}
                  onClick={() => {
                    setViewMode('deleted');
                    setPaginationModel((prev) => ({ ...prev, page: 0 }));
                    setSelectionModel([]);
                  }}
                >
                  {t('shipmentPieces.deletedTab')}
                </Button>
              </Box>
              {viewMode === 'deleted' && (
                <Typography level="body-sm" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
                  {t('shipmentPieces.deletedHint')}
                </Typography>
              )}
            </Box>

            {viewMode === 'deleted' && (
              <PermissionGate permission={PERMISSIONS.SHIPMENT_PIECES_RECOVER}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 1 }}>
                  <Tooltip
                    title={
                      selectionModel.length === 0
                        ? t('shipmentPieces.recoverSelectFirst')
                        : t('shipmentPieces.recoverCountSelected', { count: selectionModel.length })
                    }
                  >
                    <span>
                      <Button
                        size="sm"
                        variant="soft"
                        color="success"
                        startDecorator={<RestoreFromTrashIcon />}
                        disabled={selectionModel.length === 0}
                        loading={recoverLoading}
                        onClick={() => setOpenRecoverAlert(true)}
                      >
                        {t('shipmentPieces.recoverSelected')}
                      </Button>
                    </span>
                  </Tooltip>
                  {selectionModel.length > 0 && (
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {t('shipments.selectedCount', { count: selectionModel.length })}
                    </Typography>
                  )}
                </Box>
              </PermissionGate>
            )}

            <Accordion defaultExpanded={typeof window !== 'undefined' && window.innerWidth > 600}>
              <AccordionSummary
                expandIcon={<ExpandMore />}
                aria-controls="piece-filter-content"
                id="piece-filter-header"
              >
                <Typography level="h3">{t('shipmentPieces.filterTitle')}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ overflow: 'visible' }}>
                <Grid container spacing={1} sx={{ flexGrow: 1, mb: 1, overflow: 'visible' }}>
                  <Grid xs={12} sm={6} md={3}>
                    <CustomTextField
                      type="text"
                      value={shipmentId}
                      onChange={(e) => setShipmentId(e.target.value)}
                      label={t('shipmentPieces.filterShipmentId')}
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={3}>
                    <CustomTextField
                      type="text"
                      value={barcodeContains}
                      onChange={(e) => setBarcodeContains(e.target.value)}
                      label={t('shipmentPieces.filters.barcode')}
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={3}>
                    <CustomTextField
                      type="text"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      label={t('shipmentPieces.filterSearch')}
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={3}>
                    <StatusOptionList
                      label={t('shipmentPieces.filters.status')}
                      required={false}
                      status={status}
                      setStatus={setStatus}
                      showAll
                      disabled={false}
                      includePartialStates
                    />
                  </Grid>
                  {canListEventTypes && (
                    <Grid xs={12} sm={6} md={3}>
                      <TrackingEventTypeOptionList
                        label={t('shipmentPieces.filters.eventType')}
                        required={false}
                        defaultValue={eventTypeFilter}
                        onChange={(_, value) => setEventTypeFilter(value ?? null)}
                      />
                    </Grid>
                  )}
                  {canListLocations && (
                    <Grid xs={12} sm={6} md={3}>
                      <LocationOptionList
                        label={t('shipmentPieces.filters.location')}
                        defaultValue={currentLocation}
                        onChange={(_, value) => setCurrentLocation(value ?? null)}
                      />
                    </Grid>
                  )}
                  <Grid xs={12}>
                    <Box sx={{ display: 'flex', pt: 1 }}>
                      <Button type="submit" loading={isLoading}>
                        {t('shipments.search')}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>

            {viewMode === 'active' && (
              <Grid mt={2} container spacing={2} justifyContent="flex-end">
                <Grid
                  container
                  spacing={1}
                  sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start' }}
                >
                  {(canPrintLabel || canPrintBatch) && (
                    <Grid>
                      <Tooltip
                        title={
                          selectionModel.length === 0
                            ? t('shipmentPieces.tooltipPrintSelect')
                            : t('shipments.tooltipPrintStickers')
                        }
                      >
                        <span>
                          <IconButton
                            onClick={() => setPiecesStickersWindowOpened(true)}
                            disabled={selectionModel.length === 0 || !canPrintSelection}
                            color="info"
                            size="large"
                            sx={{
                              mr: { xs: 0, md: 1 },
                              width: { xs: '100%', md: 'auto' },
                            }}
                          >
                            <SubtitlesIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Grid>
                  )}
                  <PermissionGate permission={PERMISSIONS.SHIPMENT_PIECES_DELETE}>
                    <Grid>
                      <Tooltip title={t('shipments.tooltipDelete')}>
                        <IconButton
                          onClick={() => setDeleteAlert(true)}
                          disabled={selectionModel.length === 0}
                          color="danger"
                          size="large"
                          sx={{ mr: { xs: 0, md: 1 } }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </PermissionGate>
                  <PermissionGate permission={PERMISSIONS.SHIPMENT_PIECES_CREATE}>
                    <Grid>
                      <Tooltip title={t('shipmentPieces.tooltipAdd')}>
                        <IconButton
                          onClick={() => setCreateOpen(true)}
                          color="primary"
                          size="large"
                        >
                          <Add />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </PermissionGate>
                </Grid>
              </Grid>
            )}

            <Box sx={{ mt: 2 }}>
              <DataGrid
                slots={dataGridNativeCheckboxSlots}
                checkboxSelection={viewMode === 'active' || (viewMode === 'deleted' && canRecover)}
                onRowSelectionModelChange={(newSelection) => setSelectionModel(newSelection)}
                rowSelectionModel={selectionModel}
                onPaginationModelChange={(params) => {
                  if (params.pageSize !== paginationModel.pageSize) {
                    handlePageSizeChange(params.pageSize);
                  } else {
                    handlePageChange(params.page);
                  }
                }}
                pagination
                paginationModel={paginationModel}
                paginationMode="server"
                rowCount={data?.count ?? 0}
                rows={results || []}
                columns={viewMode === 'deleted' ? columnsDeleted : columns}
                loading={isLoading}
                pageSizeOptions={[25, 50, 100]}
                hideFooterSelectedRowCount
                getRowHeight={() => 'auto'}
                getRowClassName={() => (viewMode === 'deleted' ? 'deleted-piece-row' : '')}
                sx={{
                  ...dataGridSx,
                  ...(viewMode === 'deleted'
                    ? {
                        '& .deleted-piece-row': {
                          backgroundColor: 'rgba(0,0,0,0.02)',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
                        },
                      }
                    : {}),
                }}
              />
            </Box>

            <ShipmentPiecesStickersWindow
              pieces={selectedPieces}
              open={piecesStickersWindowOpened}
              onClose={() => setPiecesStickersWindowOpened(false)}
            />

            <DeleteAlert
              label={t('shipmentPieces.deleteAlertPieces')}
              open={deleteAlert}
              onDelete={() => deletePiecesFromTable(selectionModel)}
              onClose={() => setDeleteAlert(false)}
              loading={deleteLoading}
            />
            <DeleteAlert
              label={t('shipmentPieces.deleteAlertPiece')}
              open={deleteAlertSingle}
              onDelete={() => deletePiecesFromTable(selectedPieceIds)}
              onClose={() => setDeleteAlertSingle(false)}
              loading={deleteLoading}
            />

            <Modal open={openRecoverAlert} onClose={() => !recoverLoading && setOpenRecoverAlert(false)}>
              <ModalDialog variant="outlined" role="alertdialog">
                <DialogTitle>
                  <RestoreFromTrashIcon />
                  {t('shipmentPieces.recoverModalTitle')}
                </DialogTitle>
                <Divider />
                <DialogContent>
                  {t('shipmentPieces.recoverModalBody', { count: selectionModel.length })}
                </DialogContent>
                <DialogActions>
                  <Button
                    loading={recoverLoading}
                    variant="solid"
                    color="success"
                    startDecorator={<RestoreFromTrashIcon />}
                    onClick={handleRecoverPieces}
                  >
                    {t('shipmentPieces.recoverButton')}
                  </Button>
                  <Button
                    variant="plain"
                    color="neutral"
                    disabled={recoverLoading}
                    onClick={() => setOpenRecoverAlert(false)}
                  >
                    {t('actions.cancel')}
                  </Button>
                </DialogActions>
              </ModalDialog>
            </Modal>

            <CreateShipmentPieceModal
              open={createOpen}
              onClose={() => setCreateOpen(false)}
              onCreated={() => {
                setCreateOpen(false);
                setResponseMessage(t('shipmentPieces.createdSuccess'));
                setSuccessNotification(true);
                fetchData(true);
              }}
            />

            <Modal open={Boolean(editPiece)} onClose={closeEdit}>
              <ModalDialog
                variant="outlined"
                sx={{ maxWidth: 600, width: '100%' }}
                role="dialog"
              >
                <DialogTitle>
                  {editPiece?.human_label || (editPiece ? `#${editPiece.id}` : '')}
                </DialogTitle>
                <ModalClose onClick={closeEdit} />
                <Divider />
                <DialogContent>
                  <Typography level="body-xs" sx={{ fontFamily: 'monospace', color: 'text.tertiary', mb: 1 }}>
                    {editPiece?.barcode}
                    {editPiece?.barcode ? ` · ${t('shipmentPieces.sequenceBarcodeHint')}` : ''}
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography level="body-xs" sx={{ mb: 0.5 }}>
                        {t('shipmentPieces.sequence')}
                      </Typography>
                      <Input
                        type="number"
                        value={editForm.sequence || ''}
                        onChange={(e) => setEditForm({ ...editForm, sequence: e.target.value })}
                        disabled={!canUpdate}
                        color={editFieldErrors.sequence ? 'danger' : 'neutral'}
                      />
                      {editFieldErrors.sequence ? (
                        <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                          {editFieldErrors.sequence}
                        </Typography>
                      ) : (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                          {t('shipmentPieces.sequenceEditHint')}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography level="body-xs" sx={{ mb: 0.5 }}>
                        {t('actions.description')}
                      </Typography>
                      <Textarea
                        minRows={2}
                        value={editForm.description || ''}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        disabled={!canUpdate}
                      />
                    </Box>
                    <Grid container spacing={1}>
                      <Grid xs={12} sm={6}>
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          {t('shipmentPieces.weight')}
                        </Typography>
                        <Input
                          type="number"
                          value={editForm.weight_kg || ''}
                          onChange={(e) => setEditForm({ ...editForm, weight_kg: e.target.value })}
                          disabled={!canUpdate}
                          color={editFieldErrors.weight_kg ? 'danger' : 'neutral'}
                        />
                        {editFieldErrors.weight_kg && (
                          <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                            {editFieldErrors.weight_kg}
                          </Typography>
                        )}
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          {t('shipmentPieces.dimensions')}
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                          <Input
                            size="sm"
                            placeholder="W"
                            type="number"
                            value={editForm.width_cm || ''}
                            onChange={(e) => setEditForm({ ...editForm, width_cm: e.target.value })}
                            disabled={!canUpdate}
                          />
                          <Input
                            size="sm"
                            placeholder="L"
                            type="number"
                            value={editForm.length_cm || ''}
                            onChange={(e) => setEditForm({ ...editForm, length_cm: e.target.value })}
                            disabled={!canUpdate}
                          />
                          <Input
                            size="sm"
                            placeholder="H"
                            type="number"
                            value={editForm.height_cm || ''}
                            onChange={(e) => setEditForm({ ...editForm, height_cm: e.target.value })}
                            disabled={!canUpdate}
                          />
                        </Stack>
                        {editFieldErrors.dimensions && (
                          <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                            {editFieldErrors.dimensions}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid xs={12} sm={6}>
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          {t('shipmentPieces.volumetricWeightOverride')}
                        </Typography>
                        <Input
                          type="number"
                          value={editForm.volumetric_weight_kg || ''}
                          onChange={(e) =>
                            setEditForm({ ...editForm, volumetric_weight_kg: e.target.value })
                          }
                          disabled={!canUpdate}
                          placeholder={
                            editVolumetricPreview != null
                              ? `auto ≈ ${editVolumetricPreview.toFixed(3)}`
                              : ''
                          }
                        />
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          {t('shipmentPieces.chargeableWeight')}
                        </Typography>
                        <Input
                          readOnly
                          value={
                            editPiece?.chargeable_weight_kg != null &&
                            editPiece?.chargeable_weight_kg !== ''
                              ? `${editPiece.chargeable_weight_kg} kg`
                              : editChargeablePreview != null
                                ? `${editChargeablePreview.toFixed(3)} kg (${t('shipmentPieces.chargeablePreviewHint')})`
                                : '—'
                          }
                        />
                      </Grid>
                    </Grid>
                    <StatusOptionList
                      status={editForm.status}
                      setStatus={(v) => setEditForm({ ...editForm, status: v })}
                      allowEmpty
                      disabled={!canUpdate}
                      label={t('shipmentPieces.status')}
                    />
                    {canListEventTypes && (
                      <TrackingEventTypeOptionList
                        label={t('shipmentPieces.eventType')}
                        required={false}
                        disabled={!canUpdate}
                        defaultValue={editEventTypeOpt}
                        onChange={(_, v) => {
                          setEditEventTypeOpt(v ?? null);
                          setEditForm({ ...editForm, event_type: (v && v.code) || '' });
                        }}
                      />
                    )}
                    <LocationOptionList
                      defaultValue={editLocation}
                      onChange={(_, v) => setEditLocation(v ?? null)}
                      disabled={!canUpdate}
                      label={t('shipmentPieces.location')}
                    />
                    <Grid container spacing={1}>
                      <Grid xs={12} sm={6}>
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          {t('shipmentPieces.productCategoryCode')}
                        </Typography>
                        <Input
                          value={editForm.product_category_code || ''}
                          onChange={(e) =>
                            setEditForm({ ...editForm, product_category_code: e.target.value })
                          }
                          disabled={!canUpdate}
                        />
                      </Grid>
                      <Grid xs={12} sm={6}>
                        <Typography level="body-xs" sx={{ mb: 0.5 }}>
                          {t('shipmentPieces.meterPriceOverride')}
                        </Typography>
                        <Input
                          type="number"
                          value={editForm.meter_price_override || ''}
                          onChange={(e) =>
                            setEditForm({ ...editForm, meter_price_override: e.target.value })
                          }
                          disabled={!canUpdate}
                          endDecorator={<OmrSymbol height={14} />}
                        />
                      </Grid>
                    </Grid>
                    <Box>
                      <Typography level="body-xs" sx={{ mb: 0.5 }}>
                        {t('shipmentPieces.extraCost')}
                      </Typography>
                      <Input
                        type="number"
                        value={editForm.extra_cost || ''}
                        onChange={(e) => setEditForm({ ...editForm, extra_cost: e.target.value })}
                        disabled={!canUpdate}
                        endDecorator={<OmrSymbol height={14} />}
                      />
                      {editPiece?.price != null && editPiece?.price !== '' && (
                        <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                          {t('shipmentPieces.piecePrice')}: {editPiece.price}
                        </Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography level="body-xs" sx={{ mb: 0.5 }}>
                        {t('actions.notes')}
                      </Typography>
                      <Textarea
                        minRows={2}
                        value={editForm.note || ''}
                        onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                        disabled={!canUpdate}
                      />
                    </Box>
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button variant="plain" color="neutral" onClick={closeEdit} disabled={savingEdit}>
                    {t('actions.cancel')}
                  </Button>
                  <PermissionGate permission={PERMISSIONS.SHIPMENT_PIECES_UPDATE}>
                    <Button color="primary" onClick={saveEdit} loading={savingEdit}>
                      {t('actions.save')}
                    </Button>
                  </PermissionGate>
                </DialogActions>
              </ModalDialog>
            </Modal>

            <Snackbar
              autoHideDuration={3000}
              open={successDelete}
              variant="soft"
              color="success"
              onClose={(_, reason) => {
                if (reason === 'clickaway') return;
                setSuccessDelete(false);
              }}
            >
              {t('shipmentPieces.deletedSuccess')}
            </Snackbar>
            <Snackbar
              autoHideDuration={3000}
              open={failedDelete}
              variant="soft"
              color="danger"
              onClose={(_, reason) => {
                if (reason === 'clickaway') return;
                setFailedDelete(false);
              }}
            >
              {deleteErrorMsg}
            </Snackbar>
            <Snackbar
              autoHideDuration={3000}
              open={successNotification}
              variant="soft"
              color="success"
              onClose={(_, reason) => {
                if (reason === 'clickaway') return;
                setSuccessNotification(false);
              }}
            >
              {responseMessage}
            </Snackbar>
            <Snackbar
              autoHideDuration={3000}
              open={failedNotification}
              variant="soft"
              color="danger"
              onClose={(_, reason) => {
                if (reason === 'clickaway') return;
                setFailedNotification(false);
              }}
            >
              {responseMessage}
            </Snackbar>
          </Box>
        </Box>
      </form>
      {trackingPromptModal}
    </DirectionalMaterialThemeProvider>
  );
}
